import React, { useState, useEffect } from 'react';
import {
  Search,
  Check,
  X,
  Eye,
  Trophy,
  Calendar
} from 'lucide-react';

// Mock data - will be replaced with API calls
const mockAchievementStats = {
  pending: 3,
  approved: 2,
  rejected: 1
};

const mockAchievements = {
  pending: [
    {
      id: '1',
      title: 'Regional Chess Champion',
      user: 'Alex Johnson',
      sport: 'Chess',
      status: 'pending' as const,
      description: 'Winner of the 2024 Regional Chess Championship held in Central District.',
      submittedAt: '2024-11-20T10:30:00Z',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: '2',
      title: 'Tennis Club Champion',
      user: 'David Chen',
      sport: 'Tennis',
      status: 'pending' as const,
      description: 'Winner of the Annual Tennis Club Championship 2024.',
      submittedAt: '2024-11-15T09:20:00Z',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: '3',
      title: 'Best Bowler Award',
      user: 'Emily Davis',
      sport: 'Cricket',
      status: 'pending' as const,
      description: 'Best bowling figures in the Women\'s Cricket Championship.',
      submittedAt: '2024-11-08T16:45:00Z',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face'
    }
  ],
  approved: [
    {
      id: '4',
      title: 'Swimming Gold Medal',
      user: 'Sarah Wilson',
      sport: 'Swimming',
      status: 'approved' as const,
      description: 'Gold medal winner in 100m freestyle at State Championships.',
      submittedAt: '2024-11-01T14:15:00Z',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: '5',
      title: 'Football MVP',
      user: 'Mike Rodriguez',
      sport: 'Football',
      status: 'approved' as const,
      description: 'Most Valuable Player in the Regional Football League.',
      submittedAt: '2024-10-28T11:30:00Z',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face'
    }
  ],
  rejected: [
    {
      id: '6',
      title: 'Basketball High Score',
      user: 'Tom Anderson',
      sport: 'Basketball',
      status: 'rejected' as const,
      description: 'Highest individual score in local basketball tournament.',
      submittedAt: '2024-10-25T13:45:00Z',
      avatar: null
    }
  ]
};

type Achievement = {
  id: string;
  title: string;
  user: string;
  sport: string;
  status: 'pending' | 'approved' | 'rejected';
  description: string;
  submittedAt: string;
  avatar: string | null;
};

const AdminAchievements: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [playerFilter, setPlayerFilter] = useState('');
  const [allPlayers, setAllPlayers] = useState<string[]>([]);
  const [stats, setStats] = useState(mockAchievementStats);
  const [achievements, setAchievements] = useState<{
    pending: Achievement[];
    approved: Achievement[];
    rejected: Achievement[];
  }>(mockAchievements);
  const [loading, setLoading] = useState(false);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-green-100 text-green-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Swimming': 'bg-cyan-100 text-cyan-800',
      'Cricket': 'bg-yellow-100 text-yellow-800',
      'Tennis': 'bg-orange-100 text-orange-800',
      'Basketball': 'bg-purple-100 text-purple-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB') + ' at ' + date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Extract unique players from achievements
  useEffect(() => {
    const players = new Set<string>();
    Object.values(achievements).flat().forEach(achievement => {
      if (achievement.user) players.add(achievement.user);
    });
    setAllPlayers(Array.from(players).sort());
  }, [achievements]);

  const currentAchievements = achievements[activeTab] || [];
  const filteredAchievements = currentAchievements.filter(achievement => {
    const matchesSearch = achievement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      achievement.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      achievement.sport.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlayer = !playerFilter || achievement.user.toLowerCase().includes(playerFilter.toLowerCase());
    return matchesSearch && matchesPlayer;
  });

  const handleApprove = async (achievementId: string) => {
    if (window.confirm('Approve this achievement?')) {
      // Move from pending to approved
      const achievement = achievements.pending.find(a => a.id === achievementId);
      if (achievement) {
        setAchievements(prev => ({
          ...prev,
          pending: prev.pending.filter(a => a.id !== achievementId),
          approved: [...prev.approved, { ...achievement, status: 'approved' as const }]
        }));
        setStats(prev => ({ ...prev, pending: prev.pending - 1, approved: prev.approved + 1 }));
      }
    }
  };

  const handleReject = async (achievementId: string) => {
    if (window.confirm('Reject this achievement?')) {
      // Move from pending to rejected
      const achievement = achievements.pending.find(a => a.id === achievementId);
      if (achievement) {
        setAchievements(prev => ({
          ...prev,
          pending: prev.pending.filter(a => a.id !== achievementId),
          rejected: [...prev.rejected, { ...achievement, status: 'rejected' as const }]
        }));
        setStats(prev => ({ ...prev, pending: prev.pending - 1, rejected: prev.rejected + 1 }));
      }
    }
  };

  const handleViewDetails = (achievementId: string) => {
    console.log('View details for achievement:', achievementId);
    // Open modal or navigate to detail page
  };

  const UserAvatar: React.FC<{ user: any; size?: 'sm' | 'md' }> = ({ user, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-12 h-12 text-sm'
    };

    return (
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${
        user.avatar ? '' : 'bg-gray-200 text-gray-600 font-medium'
      }`}>
        {user.avatar ? (
          <img src={user.avatar} alt={user.user} className="w-full h-full rounded-full object-cover" />
        ) : (
          getInitials(user.user)
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-900">Achievements</h1>
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              {stats.pending} Pending
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {stats.approved} Approved
            </span>
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              {stats.rejected} Rejected
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Player Filter Dropdown */}
          <div className="w-48">
            <select
              value={playerFilter}
              onChange={(e) => setPlayerFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All Players</option>
              {allPlayers.map((player) => (
                <option key={player} value={player}>
                  {player}
                </option>
              ))}
            </select>
          </div>
          {/* Search Input */}
          <div className="w-80">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by user, sport, or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Segmented Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending Verification {stats.pending}
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'approved'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Approved {stats.approved}
        </button>
        <button
          onClick={() => setActiveTab('rejected')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'rejected'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Rejected {stats.rejected}
        </button>
      </div>

      {/* Achievements List */}
      <div className="space-y-4">
        {filteredAchievements.map((achievement) => (
          <div key={achievement.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              {/* Achievement Info */}
              <div className="flex items-start space-x-4 flex-1">
                <UserAvatar user={achievement} />
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{achievement.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSportBadgeColor(achievement.sport)}`}>
                      #{achievement.sport}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(achievement.status)}`}>
                      {achievement.status.charAt(0).toUpperCase() + achievement.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">{achievement.user}</p>
                  <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Submitted: {formatDateTime(achievement.submittedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 ml-4">
                {activeTab === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(achievement.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Approve"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleReject(achievement.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Reject"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleViewDetails(achievement.id)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAchievements.length === 0 && (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No achievements found</h3>
          <p className="text-gray-500">
            {searchTerm 
              ? 'Try adjusting your search criteria.' 
              : `No ${activeTab} achievements at the moment.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminAchievements;